import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LocalStorageService } from '../../utils/LocalStorageService';
import { MessageService } from 'primeng/api';

export interface FormSection {
  title: string;
  fields: { label: string; value?: string; fieldKey?: string }[];
}

export interface PDFFormConfig {
  formTitle: string;
  fileName: string;
  sections: FormSection[];
  showSignature?: boolean;
  watermarkText?: string;
  data?: any; // Student/Lead data for pre-filling
}

@Injectable({
  providedIn: 'root'
})
export class PdfFormGeneratorService {

  constructor(
    private localStorage: LocalStorageService,
    private messageService: MessageService
  ) {}

  /**
   * Generate and download a PDF form with consistent styling
   * @param config Configuration object containing form details
   */
  generatePDFForm(config: PDFFormConfig): void {
    const doc = new jsPDF();
    
    // Get organization logo from localStorage or use default
    const organizationLogo = this.localStorage.getClientLogo() || '/assets/logo.png';
    
    // Try to load and add logo
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = organizationLogo;
    
    img.onload = () => {
      this.generatePDFContent(doc, img, config);
    };
    
    img.onerror = () => {
      // If organization logo fails, try default logo
      const defaultImg = new Image();
      defaultImg.src = '/assets/logo.png';
      defaultImg.onload = () => {
        this.generatePDFContent(doc, defaultImg, config);
      };
      defaultImg.onerror = () => {
        // If all logos fail, generate without logo
        this.generatePDFContent(doc, null, config);
      };
    };
  }

  /**
   * Internal method to generate PDF content with styling
   */
  private generatePDFContent(doc: jsPDF, logoImg: any, config: PDFFormConfig): void {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let currentY = 50; // Start position after header with more spacing
    
    // Draw header on first page
    this.drawHeader(doc, logoImg, config.formTitle, pageWidth);
    
    // Process each section
    config.sections.forEach((section, sectionIndex) => {
      // Check if we need a new page
      if (currentY > pageHeight - 80) {
        doc.addPage();
        this.drawHeader(doc, logoImg, config.formTitle, pageWidth);
        currentY = 50;
      }
      
      // Draw section header
      doc.setFillColor(30, 58, 138); // Dark blue
      doc.rect(15, currentY, pageWidth - 30, 10, 'F');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(section.title, 17, currentY + 6.5);
      currentY += 16; // More spacing after section header
      
      // Draw fields in grid layout (2 columns)
      const fields = section.fields;
      for (let i = 0; i < fields.length; i += 2) {
        // Check if we need a new page
        if (currentY > pageHeight - 60) {
          doc.addPage();
          this.drawHeader(doc, logoImg, config.formTitle, pageWidth);
          currentY = 50;
        }
        
        const field1 = fields[i];
        const field2 = fields[i + 1];
        
        const columnWidth = (pageWidth - 38) / 2; // Consistent gap between columns
        const leftX = 15;
        const rightX = leftX + columnWidth + 8; // 8px gap between columns
        
        // Draw first field (left column)
        this.drawFormField(doc, field1.label, field1.value || '', leftX, currentY, columnWidth);
        
        // Draw second field (right column) if exists
        if (field2) {
          this.drawFormField(doc, field2.label, field2.value || '', rightX, currentY, columnWidth);
        }
        
        currentY += 15; // Consistent spacing between rows
      }
      
      currentY += 8; // Gap between sections
    });
    
    // Store the final Y position for signature section
    (doc as any).lastAutoTable = { finalY: currentY };
    
    // Add footer on all pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.drawFooter(doc, pageWidth);
    }
    
    // Add signature section if enabled
    if (config.showSignature !== false) {
      this.addSignatureSection(doc);
    }
    
    // Add watermark/note at bottom
    this.addWatermark(doc, config.watermarkText);
    
    // Save the PDF
    doc.save(config.fileName);

    this.messageService.clear();
    
    this.messageService.add({ 
      severity: 'success', 
      summary: 'Download Successful', 
      detail: `${config.formTitle} PDF downloaded successfully!` 
    });
  }

  /**
   * Draw a form field with label and value box
   */
  private drawFormField(doc: jsPDF, label: string, value: string, x: number, y: number, width: number): void {
    // Draw label
    doc.setFontSize(8.5);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'bold');
    doc.text(label, x, y + 2.5);
    
    // Draw value box with consistent height
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.rect(x, y + 4, width, 8);
    
    // Draw value text
    if (value) {
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      // Truncate text if too long
      const maxWidth = width - 4;
      let displayValue = value;
      const textWidth = doc.getTextWidth(displayValue);
      if (textWidth > maxWidth) {
        while (doc.getTextWidth(displayValue + '...') > maxWidth && displayValue.length > 0) {
          displayValue = displayValue.slice(0, -1);
        }
        displayValue += '...';
      }
      doc.text(displayValue, x + 2, y + 9.5);
    }
  }

  /**
   * Draw header with logo and title on every page
   */
  private drawHeader(doc: jsPDF, logoImg: any, formTitle: string, pageWidth: number): void {
    // Draw header background
    doc.setFillColor(30, 58, 138); // Dark blue
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    // Add a lighter blue strip
    doc.setFillColor(59, 130, 246); // Light blue
    doc.rect(0, 35, pageWidth, 2, 'F');
    
    // Add organization logo - maintain aspect ratio and position on right side
    if (logoImg) {
      try {
        // Calculate logo dimensions while maintaining aspect ratio
        const maxLogoWidth = 40;
        const maxLogoHeight = 25;
        const imgWidth = logoImg.width;
        const imgHeight = logoImg.height;
        const aspectRatio = imgWidth / imgHeight;
        
        let logoWidth = maxLogoWidth;
        let logoHeight = maxLogoWidth / aspectRatio;
        
        // If height exceeds max, adjust based on height instead
        if (logoHeight > maxLogoHeight) {
          logoHeight = maxLogoHeight;
          logoWidth = maxLogoHeight * aspectRatio;
        }
        
        // Position logo on the right side of header
        const logoX = pageWidth - logoWidth - 15; // 15px margin from right
        const logoY = (35 - logoHeight) / 2; // Center vertically in header
        
        doc.addImage(logoImg, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } catch (error) {
        console.error('Error adding logo on page:', error);
      }
    }
    
    // Header text - Form title (moved to left)
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255); // White text
    doc.setFont('helvetica', 'bold');
    doc.text(formTitle, 15, 22, { align: 'left' });
  }

  /**
   * Draw footer with page numbers and date
   */
  private drawFooter(doc: jsPDF, pageWidth: number): void {
    const pageCount = doc.getNumberOfPages();
    const pageSize = doc.internal.pageSize;
    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
    
    // Footer background
    doc.setFillColor(248, 250, 252);
    doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
    
    // Footer line
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
    
    // Page number
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text(
      'Page ' + doc.getCurrentPageInfo().pageNumber + ' of ' + pageCount,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    
    // Date on footer
    const currentDate = new Date().toLocaleDateString();
    doc.text(currentDate, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }

  /**
   * Add signature section at the end of the document
   */
  private addSignatureSection(doc: jsPDF): void {
    const finalY = (doc as any).lastAutoTable.finalY || 42;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Calculate available space - accounting for footer (20px) and watermark (15px)
    const spaceNeeded = 60; // signature boxes height
    const availableSpace = pageHeight - finalY - 45; // 45 = footer(20) + watermark(15) + margin(10)
    
    // Only add signature if there's enough space on current page
    if (availableSpace >= spaceNeeded) {
      // Draw signature box
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.roundedRect(15, finalY + 15, (pageWidth - 40) / 2, 35, 2, 2, 'S');
      doc.roundedRect(pageWidth / 2 + 5, finalY + 15, (pageWidth - 40) / 2, 35, 2, 2, 'S');
      
      // Labels
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      doc.text('Date:', 20, finalY + 23);
      doc.text('Applicant Signature:', pageWidth / 2 + 10, finalY + 23);
      
      // Signature lines
      doc.setDrawColor(150, 150, 150);
      doc.line(20, finalY + 43, 20 + (pageWidth - 40) / 2 - 10, finalY + 43);
      doc.line(pageWidth / 2 + 10, finalY + 43, pageWidth - 20, finalY + 43);
    } else {
      // Add to new page if not enough space
      doc.addPage();
      const newPageY = 50; // Start position on new page
      
      // Draw signature box
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.roundedRect(15, newPageY, (pageWidth - 40) / 2, 35, 2, 2, 'S');
      doc.roundedRect(pageWidth / 2 + 5, newPageY, (pageWidth - 40) / 2, 35, 2, 2, 'S');
      
      // Labels
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      doc.text('Date:', 20, newPageY + 8);
      doc.text('Applicant Signature:', pageWidth / 2 + 10, newPageY + 8);
      
      // Signature lines
      doc.setDrawColor(150, 150, 150);
      doc.line(20, newPageY + 28, 20 + (pageWidth - 40) / 2 - 10, newPageY + 28);
      doc.line(pageWidth / 2 + 10, newPageY + 28, pageWidth - 20, newPageY + 28);
    }
  }

  /**
   * Add watermark text at the bottom of the document
   */
  private addWatermark(doc: jsPDF, customText?: string): void {
    const finalY = (doc as any).lastAutoTable.finalY || 42;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Calculate position with more space - add minimum 75px gap after table/signature
    const minGapAfterContent = 75;
    const watermarkY = Math.min(finalY + minGapAfterContent, pageHeight - 30);
    
    // Only add if there's enough space, otherwise skip or add to new page
    if (watermarkY < pageHeight - 25) {
      const watermarkText = customText || 'This is an official form. Please ensure all information is accurate and complete.';
      
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.setFont('helvetica', 'italic');
      doc.text(watermarkText, pageWidth / 2, watermarkY, { align: 'center' });
    }
  }

  /**
   * Helper method to create standard form sections
   */
  createFormSections(fieldsConfig: { [sectionName: string]: string[] }, dynamicFields?: any[]): FormSection[] {
    const sections: FormSection[] = [];
    
    for (const [sectionName, fields] of Object.entries(fieldsConfig)) {
      sections.push({
        title: sectionName,
        fields: fields.map(label => ({ label, value: '' }))
      });
    }
    
    // Add dynamic fields if provided
    if (dynamicFields && dynamicFields.length > 0) {
      sections.push({
        title: 'ADDITIONAL INFORMATION',
        fields: dynamicFields.map((field: any) => ({ label: field.label, value: '' }))
      });
    }
    
    return sections;
  }

  /**
   * Helper method to create form sections with data mapping for pre-filling
   */
  createFormSectionsWithData(
    fieldsConfig: { [sectionName: string]: { label: string; key: string }[] }, 
    data: any, 
    dynamicFields?: any[]
  ): FormSection[] {
    const sections: FormSection[] = [];
    
    for (const [sectionName, fields] of Object.entries(fieldsConfig)) {
      sections.push({
        title: sectionName,
        fields: fields.map(field => ({
          label: field.label,
          value: this.getNestedValue(data, field.key) || '',
          fieldKey: field.key
        }))
      });
    }
    
    // Add dynamic fields if provided
    if (dynamicFields && dynamicFields.length > 0 && data?.dynamic_fields) {
      sections.push({
        title: 'ADDITIONAL INFORMATION',
        fields: dynamicFields.map((field: any) => ({
          label: field.label,
          value: data.dynamic_fields[field.field_name] || '',
          fieldKey: field.field_name
        }))
      });
    }
    
    return sections;
  }

  /**
   * Get nested object value by dot notation key
   */
  private getNestedValue(obj: any, key: string): any {
    if (!obj || !key) return '';
    
    // Handle direct properties
    if (obj[key] !== undefined) {
      return this.formatValue(obj[key]);
    }
    
    // Handle nested properties (e.g., 'user.fname')
    const keys = key.split('.');
    let value = obj;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return '';
    }
    
    return this.formatValue(value);
  }

  /**
   * Format value for display in PDF
   */
  private formatValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      // Format date strings
      return new Date(value).toLocaleDateString();
    }
    return String(value);
  }
}
